import {defineStore} from 'pinia'
import axios from "axios";
import type {Ref} from "vue";
import {ref} from "vue";
import type {IAuthorizationStore} from "./interfaces/IAuthorizationStore";
import type {TAuthorizationStoreDefinition} from "./types/TAuthorizationStoreDefinition";
import useWrapper from "@/composables/useWrapper";
import {ADMIN_ROLE, APP_ADMIN_ROLE, USER_ROLE} from "@/composables/useAppConst";
import {IApplicationCredentials} from "@/stores/AuthorizationStore/interfaces/IApplicationCredentials";
import type {INewToken} from "@/stores/AuthorizationStore/interfaces/IToken";
import {useApiCredentials} from "@/composables/useApiCredentials";

interface IPermission {
    use_case_id: number
    app_id: number
    id: number
    access_level: number
}

interface ICurrentApp {
    id: number
    name: string
    permissions: IPermission[]
    services: string[]
    is_app_admin: boolean
    is_global_admin: boolean
    user_use_case_id: number | null
}

export const useAuthorizationStore: TAuthorizationStoreDefinition = defineStore('authorizationStore', (): IAuthorizationStore => {
    const route: string = "/{guid}/authorize"
    const items: Ref<IApplicationCredentials[]> = ref([])
    const activeApp: Ref<IApplicationCredentials | undefined> = ref()
    const initialized: Ref<boolean> = ref(false)
    const roles: Ref<number[]> = ref([USER_ROLE])
    const services: Ref<string[]> = ref(["agent", "workflow", "guardrails", "knowledge_base", "developer_api"])
    const isAppAdmin: Ref<boolean> = ref(false)
    const isGlobalAdmin: Ref<boolean> = ref(false)
    const {
        setBearerToken, setBearerRootToken, guid, axiosBearerHeader,
        axiosBearerRefreshHeader, rootAxiosBearerRefreshHeader,
        rootGuid, rootAxiosHeader, axiosHeader, rootAxiosBearerHeader,
        portalBaseUrl
    } = useApiCredentials();

    const {handleAxiosError, rootAuth} = useWrapper();

    const aquire_token =
        handleAxiosError<IAuthorizationStore['aquire_token']>(
            async (scopeId: string, is_root: boolean = false): Promise<void> => {
                let token = axiosHeader
                if (is_root) {
                    token = rootAxiosHeader
                }
                const {data} = await axios.post<INewToken>(
                    `${route.replace('{guid}', scopeId)}/token`, {}, token.value)
                if (is_root) {
                    setBearerRootToken(data);
                } else {
                    setBearerToken(data);
                }
            }, {
                errorMsg: "stores.authorization.aquire.error"
            }
        )

    const currentApp = handleAxiosError(
        rootAuth(async (): Promise<void> => {
            const {data} = await axios.get<ICurrentApp>(
                `${portalBaseUrl}/${guid.value}/apps/active/current`,
                axiosBearerHeader.value
            );

            // Every user sees the USER menu group.  Admin / App-Admin
            // flags add their respective groups on top.
            const r: number[] = [USER_ROLE]
            if (data.is_global_admin) {
                r.push(ADMIN_ROLE)
            }
            if (data.is_app_admin) {
                r.push(APP_ADMIN_ROLE)
            }
            roles.value = r

            services.value = data.services ?? ["agent", "workflow", "guardrails", "knowledge_base", "developer_api"]
            isAppAdmin.value = data.is_app_admin ?? false
            isGlobalAdmin.value = data.is_global_admin ?? false
        }, false),
        {errorMsg: 'stores.application.currentApp.error'}
    )


    const refresh_token =
        handleAxiosError<IAuthorizationStore['refresh_token']>(
            async (scopeId: string, is_root: boolean = false): Promise<void> => {
                let token = axiosBearerRefreshHeader
                if (is_root) {
                    token = rootAxiosBearerRefreshHeader
                }
                const {data} = await axios.post<INewToken>(
                    `${route.replace('{guid}', scopeId)}/refresh`, {}, token.value)
                if (is_root) {
                    setBearerRootToken(data);
                } else {
                    setBearerToken(data);
                }
            }, {
                errorMsg: "stores.authorization.refresh.error"
            }
        )


    const hasService = (name: string): boolean => services.value.includes(name)

    return {
        items,
        initialized,
        aquire_token,
        activeApp,
        refresh_token,
        roles,
        services,
        isAppAdmin,
        isGlobalAdmin,
        currentApp,
        hasService
    }
});



